import { z } from "zod";
import { CreateLineRequestSchema } from "./lines.js";
import {
  CreateMachineRequestSchema,
  UpdateMachineRequestSchema,
  DuplicateMachineRequestSchema,
} from "./machines.js";
import { CreateProductRequestSchema, UpdateProductRequestSchema } from "./products.js";
import { CreateDeviceRequestSchema } from "./devices.js";
import { type CommandSpec, Uuid, hasUpdate, UPDATE_MSG } from "./assistant-catalog-types.js";

// Família Estrutura (action · GESTOR). CRUD de linhas/máquinas/produtos/CLPs/
// sensores. Cada comando reusa EXATAMENTE o service da tela via dispatch (zero lógica nova). Args
// reusam os request schemas (extend + .strict()) p/ não divergir do wire. As EXCLUSÕES + `device.
// rotateKey` são DESTRUTIVAS (confirmação reforçada — digitar o nome). Os dispatchers NUNCA enviam
// `x-force-delete`/`x-confirm-real-line`: bloqueios de domínio (LINE_RUNNING, HAS_HISTORY, DEVICE_
// IN_USE, produto em uso) re-superficiam amigáveis no card. `device.create`/`rotateKey` devolvem a
// chave UMA vez (oneTimeSecret), nunca em resultRef/audit.

export const STRUCTURE_COMMANDS: readonly CommandSpec[] = [
  {
    name: "line.create",
    kind: "action",
    riskTier: "confirm",
    minRole: "GESTOR",
    // `responsible` fica de fora do chat de propósito (nome de pessoa = PII fora do manifesto do
    // modelo); atribua o responsável pela tela. Alinha com o contrato (name/description apenas).
    description: "Cria uma linha de produção (nome/descrição).",
    argsSchema: CreateLineRequestSchema.omit({ responsible: true }).strict(),
  },
  {
    name: "line.update",
    kind: "action",
    riskTier: "confirm",
    minRole: "GESTOR",
    description: "Edita uma linha (nome/descrição). Linha em produção só pela tela.",
    argsSchema: CreateLineRequestSchema.omit({ responsible: true })
      .partial()
      .extend({ lineId: Uuid })
      .strict()
      .refine(hasUpdate(["lineId"]), UPDATE_MSG),
  },
  {
    name: "line.delete",
    kind: "action",
    riskTier: "destructive",
    minRole: "GESTOR",
    description: "Exclui uma linha de produção.",
    argsSchema: z.object({ lineId: Uuid }).strict(),
  },
  {
    name: "machine.create",
    kind: "action",
    riskTier: "confirm",
    minRole: "GESTOR",
    description: "Cria uma máquina em uma linha.",
    // Spec 080 — `model3dId` entrou em `CreateMachineRequestSchema` para o caminho de RASCUNHO
    // parar de descartá-lo, e cairia aqui de carona. Fica FORA do contrato do agente de propósito:
    // ele não tem tool que liste modelos 3D, então só poderia inventar um UUID — e alargar a
    // superfície do agente como efeito colateral de outra correção é o que o guardrail #15 existe
    // para impedir. Expor de verdade é decisão de produto, com projeção e eval próprios.
    argsSchema: CreateMachineRequestSchema.omit({ model3dId: true })
      .extend({ lineId: Uuid })
      .strict(),
  },
  {
    name: "machine.update",
    kind: "action",
    riskTier: "confirm",
    minRole: "GESTOR",
    description:
      "Edita SÓ os campos informados de uma máquina (código/nome/tipo/custo parado/exige operador). Omitir um campo o mantém — NÃO envie campos que o usuário não pediu para mudar.",
    // `requiresOperator` sem default aqui (no update, enviar = ALTERAR): o default do create vazava
    // p/ o manifesto e induzia o modelo a mandar `requiresOperator:false` e desmarcar sem querer.
    argsSchema: UpdateMachineRequestSchema.extend({
      lineId: Uuid,
      machineId: Uuid,
      requiresOperator: z.boolean().optional(),
    })
      .strict()
      .refine(hasUpdate(["lineId", "machineId"]), UPDATE_MSG),
  },
  {
    name: "machine.duplicate",
    kind: "action",
    riskTier: "confirm",
    minRole: "GESTOR",
    description: "Duplica a configuração de uma máquina.",
    argsSchema: DuplicateMachineRequestSchema.extend({ lineId: Uuid, machineId: Uuid }).strict(),
  },
  {
    name: "machine.delete",
    kind: "action",
    riskTier: "destructive",
    minRole: "GESTOR",
    description: "Exclui uma máquina.",
    argsSchema: z.object({ lineId: Uuid, machineId: Uuid }).strict(),
  },
  {
    name: "product.create",
    kind: "action",
    riskTier: "confirm",
    minRole: "GESTOR",
    description: "Cria um produto.",
    argsSchema: CreateProductRequestSchema.strict(),
  },
  {
    name: "product.update",
    kind: "action",
    riskTier: "confirm",
    minRole: "GESTOR",
    description: "Edita um produto.",
    argsSchema: UpdateProductRequestSchema.extend({ productId: Uuid })
      .strict()
      .refine(hasUpdate(["productId"]), UPDATE_MSG),
  },
  {
    name: "product.delete",
    kind: "action",
    riskTier: "destructive",
    minRole: "GESTOR",
    description: "Exclui um produto.",
    argsSchema: z.object({ productId: Uuid }).strict(),
  },
  {
    name: "device.create",
    kind: "action",
    riskTier: "confirm",
    minRole: "GESTOR",
    description: "Cadastra um CLP/Device (a chave de API é exibida uma única vez).",
    argsSchema: CreateDeviceRequestSchema.strict(),
  },
  {
    name: "device.rotateKey",
    kind: "action",
    riskTier: "destructive",
    minRole: "GESTOR",
    description: "Rotaciona a chave de API de um CLP (a nova chave é exibida uma única vez).",
    argsSchema: z.object({ deviceId: Uuid }).strict(),
  },
  {
    name: "device.delete",
    kind: "action",
    riskTier: "destructive",
    minRole: "GESTOR",
    description: "Exclui um CLP/Device.",
    argsSchema: z.object({ deviceId: Uuid }).strict(),
  },
  {
    name: "sensor.linkToParameter",
    kind: "action",
    riskTier: "confirm",
    minRole: "GESTOR",
    description: "Vincula um sensor a um parâmetro de máquina.",
    argsSchema: z.object({ parameterId: Uuid, sensorId: Uuid }).strict(),
  },
  {
    name: "sensor.unlink",
    kind: "action",
    riskTier: "confirm",
    minRole: "GESTOR",
    description: "Desvincula um sensor de um parâmetro (pelo id do vínculo).",
    argsSchema: z.object({ linkId: Uuid }).strict(),
  },
];
